import React, {
  useState,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useRouter } from "next/router";

import { pageUrl } from "@utils/product";
import { reducer } from "@reducers/product/list";
import { api } from "@utils/api";
import { listToTree } from "@utils/functions";

const CategoryContext = React.createContext();
export const CategoryProvider = ({ children, props }) => {
  const [filters, setFilters] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const categoryRef = useRef({});
  const [state, dispatch] = useReducer(reducer, {
    categories: [],
    categoryTree: [],
    loading: true,
    expand: {},
  });

  const router = useRouter();

  const loadCategory = useCallback(async () => {
    const data = {
      retailer_id: props.retailerId || null,
      brand: props.brand || null,
      name: props.filters.name || null,
      description: true,
    };

    if (props.isAuction) {
      data.auction = true;
    }

    const categories = await api({
      url: "/categories",
      method: "POST",
      data,
    });

    dispatch({
      type: "SET_DATA",

      data: {
        categories,
        categoryTree: listToTree(categories),
        loading: false,
      },
    });
  }, [props.retailerId, props.brand, props.filters.name, props.isAuction]);

  useEffect(() => {
    loadCategory();
  }, [loadCategory]);

  const updateFilter = (filters, push = false, tag = true, all = false) => {
    setFilters(filters);

    if (push) {
      const params = {};
      let slug = false;
      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];
        if (filter.type === "slug") {
          slug = filter._id;
        } else {
          if (params[filter.type]) {
            params[filter.type] += `|${filter._id}`;
          } else {
            params[filter.type] = filter._id;
          }
        }
      }

      if (props.filters.sortBy) params.sortBy = props.filters.sortBy;
      if (props.filters.name) params.name = props.filters.name;
      if (!tag && !all) {
        const hasIndexSlug = breadcrumbs.findIndex((i) => i.slug === slug);
        if (hasIndexSlug !== -1) {
          if (hasIndexSlug !== 0) {
            slug = breadcrumbs[hasIndexSlug - 1].slug;
          } else {
            slug = "NA";
          }
        }
      }

      router.push({
        pathname: pageUrl(props, slug),
        query: params,
      });
    }
  };

  const setExpand = (category, level, full = false) => {
    if (full)
      dispatch({
        type: "SET_DATA",
        data: {
          expand: category,
        },
      });
    else {
      dispatch({
        type: "SET_DATA",
        data: {
          expand: {
            ...state.expand,
            [`cl${level}`]:
              state.expand[`cl${level}`] === category.slug
                ? null
                : category.slug,
          },
        },
      });
    }
  };

  return (
    <CategoryContext.Provider
      value={{
        categories: state.categories,
        categoryTree: state.categoryTree,
        filters,
        breadcrumbs,
        loading: state.loading,
        updateFilter,
        setBreadcrumbs,
        categoryRef,
        expand: state.expand,
        setExpand,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export function useCategory() {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error("useCategory must be used within a CategoryProvider");
  }
  return context;
}
